import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import store from "../State/Store/store";
import TickerTimeout from "../utils/TickerTimeout";
import { FEATURES_CONFIG } from "../featuresConfig";
import { useReelRefs } from "./ReelRefsContext";
import { useAppActivity } from "./AppActivityContext";
import { createPlayButtonRotationAnimation } from "../utilities";
import { Settings } from "../settings";
import anime from "animejs";
import * as PIXI from "pixi.js";
import { soundManager } from "../SoundManager";
import { fsDiag, fsBlockedBy } from "../utils/fsDiag";
import { SOUND_NAME } from "../sounds";
import SpineAnimationData from "../utils/SpineAnimationData";
import { rgsPlay } from "../State/Slices/spinSlice";
import {
  setFreeSpinsSpinning,
  setFreeSpinsManualStop,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setShowFreeSpinTotalWin,
  setShowFreeSpinMaxWin,
  setShowFreeSpinZeroWin,
  setFreeSpinsSpinComplete,
  setFreeSpinsAwaitingResume,
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  openConnectionPopup,
  closeConnectionPopup,
  setReelsFrozen,
} from "../State/actions";
import {
  selectFreeSpinsActive,
  selectFreeSpinsSpinning,
  selectFreeSpinsRemaining,
  selectFreeSpinsWinLimitReached,
  selectFreeSpinsTotalWin,
  selectShowTransitionDialog,
  selectWinAnimationsPlaying,
  selectCounterAnimationFinished,
  selectCriticalAnimationPlaying,
  selectFreeSpinsSpinComplete,
  // selectFreeSpinsEntrySequencePending, // Mad Labs level-box flourish — no ladder here
  selectFreeSpinsAwaitingResume,
  selectFreeSpinsReloadPopupPending,
  selectFreeSpinsReloadPhase,
  selectIntroPopupActive,
  selectShowAdditionalFreeSpins,
  selectErrorPopupActive,
  selectErrorRecoveryTransitionActive,
} from "../State/selectors";
import { waitForBoardToSettle } from "../Games/Shared/waitForBoardToSettle";
import { createAnticipationRunner } from "../Games/Shared/anticipationRunner";
import { isTurboLike } from "../utils/speedHelpers";
import { spinHeldOff } from "../utils/spinHeldOff";
import {
  selectFreeSpinOutroPopup,
  isFreeSpinsCapReached,
  isFreeSpinsRoundFinished,
  FS_OUTRO_POPUP,
} from "../Games/Shared/freeSpinOutroPopup";

// The base game's table for a speed (FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS).
const speedConfigFor = (speed) =>
  FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[speed?.toUpperCase()] ||
  FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;

/**
 * FreeSpinBasicControlContext — the Mad Labs free spins round.
 *
 * A deliberately small sibling of ReelControlContext. That file is ~3,900
 * lines because the base game carries tension, turbo, anticipation, autoplay
 * rules, connection popups, free rounds and replay. A free spins round has
 * none of them:
 *
 *   - the player does not choose when to spin — the round runs itself
 *   - there is one command, `freespin`, and no bet to vary
 *   - there is no turbo, no hold, no slam stop. One interaction only:
 *     MANUAL STOP, from the button or the spacebar, which lands the reels
 *     sooner. Exactly what ButtonPotCollectionPlay does for Pot Collection.
 *
 * What it keeps from the base path, because that path is proven:
 *
 *   start   stagger the reels with REEL_START_DELAYS
 *   wait    the response and a minimum spin time race; the LATER one wins
 *   stop    stagger the stops with REEL_STOP_DELAYS
 *
 * ── The lifecycle ──
 *
 *   room appears  →  ENTRY_DELAY  →  spin  →  land  →  INTER_SPIN_DELAY  →  spin
 *                                     ...until freespins_left reaches 0...
 *                                 →  total win dialog  →  wipe back to base
 *
 * ── Why the guard matters more here than anywhere else ──
 *
 * `freespin` sent against a closed round returns 202, and a SECOND rejected
 * action locks the session permanently. The loop must therefore never fire on
 * a guess: every spin goes through rgsPlay, which checks
 * flow.available_actions first. See docs/api-contract-mad-labs-freespins.md §0.
 */

// How long the room sits before the first spin, so the player sees where they
// are. Pot Collection waits on a user click here; this round starts itself.
//
// Counted from the moment the WIPE FINISHES, not from when the round became
// active. `freeSpins.active` flips at the wipe's MIDPOINT — that is the whole
// point of the midpoint, the swap happens under full cover — but the clip runs
// on for another ~0.53s after it. Counting from there started the round while
// the cover was still lifting.
const ENTRY_DELAY = 1000;

// Between one spin landing and the next starting.
const INTER_SPIN_DELAY = 400;

// A press gets NO delay — see handleResume. Base ButtonPlay rotates the button
// and calls handleSpin on the same tick, and that is the feel to match.

// ── After the last spin lands, before the total win dialog takes the screen ──
//
// ONE loop of the master win animation, not the show-all window's two.
//
// The beat is still borrowed from the global win rather than being a number of
// its own — that is what makes it feel like a pause the player already knows,
// "a win is being shown, then we move on", instead of the flat 800ms it used
// to be, which read as a stall or a snap depending on what had just happened.
// But the show-all holds for TWO loops, and a losing round has nothing on
// screen to hold: the second loop is just silence before the dialog.
//
// Turbo is unaffected in practice — LOOPS_FOR_SHOW_ALL_TURBO is already 1, so
// it was one loop there before this and it is one loop now.
//
// ── Only on a LOSING last spin ──
//
// A won round has already spent its beat holding its global win, so spending
// another on top put two back to back and the round looked like it had
// forgotten to end. The caller applies this on a loss and zero on a win.
const outroDelayMs = () => SpineAnimationData.getMasterWinDuration() ?? 0;

// Exported because Stage.js MUST bridge it into the PIXI tree. @pixi/react's
// Stage is its own reconciler root, so React context does not cross it — every
// context the PIXI tree consumes has to be listed in Stage's `contexts` array.
// Left off that list, useFreeSpinBasicControl() returns null inside the tree and
// every `control?.handler?.()` in a PIXI component silently does nothing.
// Slow-network grace on top of the expected spin time, before the connection
// popup is raised. Same value and same reason as PotCollectionSpinEngine's.
const CONNECTION_TIMEOUT_BUFFER = 350;

/**
 * How long after the tab comes back a spin may still be running before it is
 * force-stopped. Matches the base game's own emergency resume timeout.
 */
const EMERGENCY_RESUME_STOP_MS = 15000;

/** visibilitychange can fire in bursts; ignore repeats inside this window. */
const TAB_RETURN_DEBOUNCE_MS = 500;

export const FreeSpinBasicControlContext = createContext(null);

export const FreeSpinBasicControlProvider = ({ children }) => {
  const dispatch = useDispatch();
  const {
    freeSpinBasicReelRefs,
    freeSpinBasicOverlayReelRefs,
    playButtonRef,
    freeSpinsGameViewControllerRef,
    freeSpinBasicDimReelSetRef,
  } = useReelRefs();
  // The waiting pulse, held so the press can retire it — PC keeps its own the
  // same way, as pulseIntervalRef.
  const pulseRef = useRef(null);

  const active = useSelector(selectFreeSpinsActive);
  const spinning = useSelector(selectFreeSpinsSpinning);
  const remaining = useSelector(selectFreeSpinsRemaining);
  // ── The cap ENDS the round ──
  //
  // Two selectors for one question, and both have to be listed or the loop
  // sleeps through the answer: `winLimitReached` is the BE flag, `totalWin` is
  // what the FE guard measures. Either can be the one that moves on the spin
  // that crosses the cap, and the loop must re-run on whichever it is.
  //
  // The values themselves are not used here — isFreeSpinsCapReached reads them
  // off the store below, so the two paths cannot answer differently. These are
  // the subscription. See isFreeSpinsCapReached for the rule.
  const winLimitReached = useSelector(selectFreeSpinsWinLimitReached);
  const fsTotalWin = useSelector(selectFreeSpinsTotalWin);
  // The scene wipe. Cleared by TransitionDialogController.finishAnimation on
  // the clip's own `complete`, so it is the honest "the cover has lifted"
  // signal — and it stays honest if the animator relengthens the clip.
  const transitionActive = useSelector(selectShowTransitionDialog);

  // ── What the round must WAIT for ──
  //
  // `spinning` clears when the reels land, which is NOT when the spin is
  // finished being presented. A Big Win takes the screen after that, and the
  // counter runs after that again. Without these the next spin fired 400ms
  // after landing, straight over the top of a celebration still playing.
  //
  // These are the same three conditions ReelControlContext.handleSpin refuses
  // a base-game spin on. Read as selectors, not from the store, so the loop
  // effect re-runs the moment the last of them clears.
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const counterFinished = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);

  // This round's OWN completion flag — the counterpart of spin.spinComplete,
  // not that flag. Raised by the last reel's stop, lowered by handleFreeSpin.
  // The win controller gates its main effect on the same one, so it is the
  // honest "this spin has been presented" signal for the loop. Base and bonus
  // keep separate pairs so neither round can drive the other.
  const spinComplete = useSelector(selectFreeSpinsSpinComplete);
  // The level boxes bank their already-won levels when the room opens. The
  // round does not begin until the last one has finished converting — driven by
  // the clips' own completion, not a delay.
  // Mad Labs only: Cleopatra's Fire has no level ladder (2026-09-29).
  // const entrySequencePending = useSelector(
  //   selectFreeSpinsEntrySequencePending,
  // );
  // A reloaded round waits for the player's press. See the slice field.
  const awaitingResume = useSelector(selectFreeSpinsAwaitingResume);
  const reloadPopupPending = useSelector(selectFreeSpinsReloadPopupPending);
  // ── Nothing is sent while an error owns the screen ──
  //
  // PotCollectionControlContext's hard block, ported. Both flags are needed:
  // WarningPopup batches closeErrorPopup with setSpinComplete in ONE callback,
  // so errorPopupActive is already false by the time this effect re-runs, and
  // errorRecoveryTransitionActive is what covers that gap.
  //
  // Without this the loop kept spinning straight through a refusal, which is
  // half of the ban path: 202, retry, 104, session dead.
  // ── Nothing runs under the boot intro ──
  //
  // The win controller has always refused to present while this is up; the
  // loop had no such gate, so on a reload it ran its whole chain against a
  // screen the player could not see — and opened the total win dialog on top
  // of the intro. PotCollectionControlContext gates on the popups that own the
  // boot for exactly this reason.
  const introPopupActive = useSelector(selectIntroPopupActive);
  // The level-up popup, gated here for the same reason the intro is.
  //
  // criticalAnimationPlaying is not enough on its own: it is one boolean with
  // eleven writers, so the level-up sequence raises it, some other owner
  // lowers it a beat later, and the loop fires a spin while the popup is
  // still on screen — which is exactly what happened. A popup that owns the
  // screen must block the loop by its OWN visibility, which nothing else can
  // clear from under it.
  const additionalFreeSpinsActive = useSelector(selectShowAdditionalFreeSpins);

  // ── Tab visibility ──
  //
  // Every other control context takes this and this one did not, which is the
  // whole reason a screenshot or a tab switch mid-spin could strand the round.
  // See the effect at the bottom of this file.
  const { isActive } = useAppActivity();
  // Which recovery the round is in. "completion" means the SCENE owns the
  // outro — see the branch in GameViewSimple.
  const reloadPhase = useSelector(selectFreeSpinsReloadPhase);
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );
  // ── An init still open (the 105 fix, from 3 Orbs of Zeus) ── a spin sent
  // beside it is answered 105. rgsPlay's condition now refuses it — and a
  // refused free spin would leave the reels started with no answer — so the
  // loop WAITS: e.g. the free spins resuming after a H&W round, while the
  // exit's syncGameState is still open. Re-runs the loop when it clears.
  const spinHeldOffNow = useSelector(spinHeldOff);
  // ── A Hold & Win won on a free spin owns the round ── from its trigger
  // (the intro is owed) until the exit wipe hands back (resetHoldAndWin). The
  // free spins wait, exactly as the base game waits: no free spin is sent —
  // the server only offers `respin` now — and no outro opens, even after the
  // last free spin (GDD "Hold & Win During Free Spins": "Keep the Free Spins
  // pack suspended throughout all respins"). Re-runs the loop when it clears.
  const holdAndWinOwnsRound = useSelector(
    (state) => state.holdAndWin.triggered || state.holdAndWin.active,
  );

  // ── Timers ──
  // Every one is a TickerTimeout, so the round pauses with the game rather
  // than with the tab — the rule this whole project follows.
  const timeoutsRef = useRef([]);
  const nextSpinTimeoutRef = useRef(null);

  // Guards. Refs, not state: the loop reads them inside callbacks that must
  // not wait for a render.
  const spinningRef = useRef(false);
  // Stops a second timer being built for the same state. NOT a substitute for
  // the effect cleanup — see the loop.
  const processingRef = useRef(false);
  const manualStopRef = useRef(false);
  // The auto stop already scheduled for this spin, if the response beat the
  // minimum spin time. Held so a manual press arriving afterwards can cancel it
  // and land the reels now — see handleManualStop.
  const pendingStopTimerRef = useRef(null);
  // ── The live per-reel stops, so a press can OVERTAKE them ──
  //
  // The base game's reelStopTimeoutsRef, copied. ReelControlContext.stopReels
  // clears this Map before it schedules, which is what lets a press arriving
  // mid-cascade re-schedule every reel still in the air onto the fast manual
  // timing. Without it the first cascade wins and the press does nothing —
  // AUTO is 0/200/400/600/800ms, so the reels crawl for 800ms while the player
  // is pressing at them. That was the "window" (reported 2026-09-10).
  const reelStopTimeoutsRef = useRef(new Map());
  // The board on screen when this spin left, so handleManualStop can tell
  // "the reply is in" from "still waiting" — freeSpins.currentRound is only
  // ever replaced, never cleared, so its identity is the only honest test.
  const roundAtStartRef = useRef(null);
  // Tab-visibility bookkeeping — see the app activity effect.
  const pausedByInactivityRef = useRef(false);
  const lastTabReturnTimeRef = useRef(0);
  const emergencyStopRef = useRef(null);
  const stoppedReelsRef = useRef(new Set());
  // ── Turbo / super turbo in free spins (2026-10-23: "works exactly the same
  // as base game") ── the speed THIS spin runs at, read once as it leaves —
  // a press of the speed button mid-spin applies from the next one. And
  // whether the turbo reels have started turning yet: super turbo waits its
  // SPIN_AFTER_WAIT_MS for the reply and, inside it, DROPS (the base game's
  // turboSpinningRef).
  const spinSpeedRef = useRef("auto");
  const turboSpinningRef = useRef(false);
  const turboSpinTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach((t) => t?.clear?.());
    timeoutsRef.current = [];
    nextSpinTimeoutRef.current?.clear?.();
    nextSpinTimeoutRef.current = null;
  }, []);

  const track = useCallback((timeout) => {
    timeoutsRef.current.push(timeout);
    return timeout;
  }, []);

  // ── Per-reel anticipation — the SAME chain as the base game ──
  //
  // GDD §6.1 (scatters) and §6.2 (coins) are both "Base Game and Free Spins":
  // three scatters retrigger, six coins win Hold & Win, here as there. The
  // chain lives in Games/Shared/anticipationRunner and both rooms drive it;
  // this is only the free-spins wiring. Its state is keyed by the round's
  // board, which rgsPlay replaces on every free spin.
  //
  // Chain stops are NOT in reelStopTimeoutsRef: stopReels clears that Map on
  // a manual press to re-schedule the cascade, and the chain reels are the
  // chain's to stop. They are tracked for clearTimers all the same.
  const anticipationChainRef = useRef(null);
  const makeAnticipationRunner = useCallback(
    () =>
      createAnticipationRunner({
        chainRef: anticipationChainRef,
        getReels: () => freeSpinBasicReelRefs.current,
        getScene: () => freeSpinsGameViewControllerRef?.current,
        getDim: () => freeSpinBasicDimReelSetRef.current,
        stopReel: (reelIndex) => {
          freeSpinBasicReelRefs.current?.[reelIndex]?.stopSpin();
        },
        track: (timeout) => {
          timeoutsRef.current.push(timeout);
        },
        untrack: (timeout) => {
          const idx = timeoutsRef.current.indexOf(timeout);
          if (idx > -1) timeoutsRef.current.splice(idx, 1);
        },
      }),
    [freeSpinBasicReelRefs, freeSpinsGameViewControllerRef, freeSpinBasicDimReelSetRef],
  );

  // ── Reels ────────────────────────────────────────────────────────────────

  /**
   * Start every reel, staggered exactly as the base game staggers them.
   *
   * ── SKIP_WIND_UP is read here, as the base game reads it ──
   *
   * This room always called `startSpin`, so its reels pulled back before every
   * spin no matter what the base game was doing — including in turbo, where
   * the base game has skipped the wind-up for as long as the mode has existed.
   * The two rooms are meant to be one machine, so the flag is honoured in both.
   *
   * The speed is read from the store rather than a selector because this runs
   * outside React's render: `startReels` is called from the round loop.
   */
  const startReels = useCallback(() => {
    // The jackpot plates the last spin lit go dark again (as the base game).
    freeSpinsGameViewControllerRef?.current?.releaseJackpotHits?.();
    const reels = freeSpinBasicReelRefs.current || [];
    // const delays = FEATURES_CONFIG.SPIN.REEL_START_DELAYS;
    // const speed = store.getState().settings?.autoPlaySpeed;
    // const speedConfig =
    //   FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[speed?.toUpperCase()] ||
    //   FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;
    // This spin's speed (spinSpeedRef) and its own start gaps — turbo starts
    // every reel together, as the base game does (2026-10-23).
    const speedConfig = speedConfigFor(spinSpeedRef.current);
    const delays = speedConfig.REEL_START_DELAYS || FEATURES_CONFIG.SPIN.REEL_START_DELAYS;
    const skipWindUp = speedConfig.SKIP_WIND_UP || false;
    const start = (reel) =>
      skipWindUp ? reel?.startSpinNoWindUp?.() : reel?.startSpin?.();

    for (let i = 0; i < reels.length; i++) {
      const delay = delays?.[i] ?? i * FEATURES_CONFIG.SPIN.REEL_START_DELAY;
      if (delay === 0) {
        start(reels[i]);
      } else {
        track(new TickerTimeout(() => start(reels[i]), delay));
      }
    }
  }, [freeSpinBasicReelRefs, freeSpinsGameViewControllerRef, track]);

  /**
   * Stop every reel, staggered.
   *
   * @param {boolean} manual Manual stops use the tighter cascade, the same
   *   two tables the base game picks between.
   */
  const stopReels = useCallback(
    (manual = false) => {
      const reels = freeSpinBasicReelRefs.current || [];
      // const delays = manual
      //   ? FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL
      //   : FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_AUTO;
      // Turbo's own stop gaps first (all together), as the base game's
      // speedConfig.REEL_STOP_DELAYS override (2026-10-23).
      const turboStops = isTurboLike(spinSpeedRef.current)
        ? speedConfigFor(spinSpeedRef.current).REEL_STOP_DELAYS
        : null;
      const delays =
        turboStops ||
        (manual
          ? FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL
          : FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_AUTO);

      // The chain for THIS free spin's board — see makeAnticipationRunner.
      // A manual press re-enters here and finds the same chain running.
      const round = store.getState().freeSpins?.currentRound;
      const anticipation = makeAnticipationRunner();
      anticipation.sync(round, round?.reels ?? null);

      // ── Cancel whatever was already in the air ──
      //
      // The base game's first act in stopReels, and the whole reason a press
      // can overtake a cascade. A reel that has already STOPPED is untouched:
      // its timeout has fired and left the Map.
      for (const timeout of reelStopTimeoutsRef.current.values()) {
        timeout?.clear?.();
      }
      reelStopTimeoutsRef.current.clear();
      // The whole-volley timer too, if one was waiting out the minimum spin.
      pendingStopTimerRef.current?.clear?.();
      pendingStopTimerRef.current = null;

      for (let i = 0; i < reels.length; i++) {
        // Every reel reports its landing peak to the chain, which starts on
        // the reel before its first anticipation reel and hands on from there.
        reels[i]?.setOnLandingPeak?.((peakReelIndex) =>
          anticipation.onLandingPeak(peakReelIndex),
        );

        // A reel the chain holds is stopped by the chain, on its turn.
        if (anticipation.holds(i)) continue;

        const delay = delays?.[i] ?? 0;
        // ── ALWAYS through the ticker, even at zero ──
        //
        // This branched: `if (delay === 0) reels[i].stopSpin()` inline, and
        // reel 0's manual delay IS 0. The base game has no such branch — every
        // one of its five stop sites goes through a TickerTimeout (see
        // ReelControlContext.stopReels and the three tension re-schedules).
        //
        // That frame is not decoration. stopReels is called from a store
        // SUBSCRIPTION, which runs inside the dispatch, before React has
        // rendered — so an inline stop began the landing while the reel still
        // held the previous free spin's board, dropping wrong symbols that were
        // then swapped when the reel's own effect caught up. A TickerTimeout
        // cannot fire before the next frame, by which time the effect has run.
        //
        // Reported 2026-09-10. Present since the import (0f7bf620), and NOT
        // manual-only: `wait` is also 0 whenever the response outran the
        // minimum spin time. Never restore the inline call.
        const timeout = new TickerTimeout(() => {
          reelStopTimeoutsRef.current.delete(i);
          reels[i]?.stopSpin();
        }, delay);
        reelStopTimeoutsRef.current.set(i, timeout);
        track(timeout);
      }
    },
    [freeSpinBasicReelRefs, track, makeAnticipationRunner],
  );

  /** True once every reel has reported itself stopped. */
  const allReelsLanded = useCallback(() => {
    const reels = freeSpinBasicReelRefs.current || [];
    if (!reels.length) return false;
    return reels.every((r) => r?.getReelSpinComplete?.() !== false);
  }, [freeSpinBasicReelRefs]);

  // ── One spin ─────────────────────────────────────────────────────────────

  /**
   * Play one free spin.
   *
   * The reels start immediately and the request goes out alongside them —
   * the same order the base game uses, so the spin never waits on the network
   * to begin moving.
   */
  const handleFreeSpin = useCallback(() => {
    const state = store.getState();
    const anim = state.animations;

    // Belt and braces against the loop's own gate — read from the store so a
    // call from anywhere else is refused too. These are the same three checks
    // ReelControlContext.handleSpin opens with.
    if (
      fsBlockedBy("handleFreeSpin", [
        ["spinningRef", spinningRef.current],
        ["!freeSpins.active", !state.freeSpins.active],
        [
          "no spins left",
          state.freeSpins.awarded - state.freeSpins.played <= 0,
        ],
        // ── Nothing is SENT once the cap is hit ──
        //
        // The loop already refuses to schedule one, but this is the choke
        // point every path goes through, including handleResume's press on a
        // reloaded round — which clears awaitingResume and calls straight in
        // here without consulting the loop. Refused here, that press simply
        // releases the round to the loop, which opens the MAX WIN outro.
        ["win cap reached", isFreeSpinsCapReached(state)],
        ["winAnimationsPlaying", anim.winAnimationsPlaying],
        [
          "counter started, not finished",
          anim.counterAnimationStarted && !anim.counterAnimationFinished,
        ],
        ["criticalAnimationPlaying", anim.criticalAnimationPlaying],
        // A Hold & Win from a free spin is on (see holdAndWinOwnsRound).
        [
          "holdAndWin",
          state.holdAndWin.triggered || state.holdAndWin.active,
        ],
      ])
    ) {
      return;
    }

    // The board must exist before a spin is requested. It is built at load and
    // only hidden by alpha, so an empty array here means something upstream
    // failed to render it — and spinning anyway would send the command, take a
    // free spin off the count, and then wait for reels that will never land.
    // Loud, because it is silent otherwise: this exact case hung the round.
    if (!(freeSpinBasicReelRefs.current || []).length) {
      console.warn(
        "[FreeSpinBasic] No reels registered — skipping spin. The bonus board " +
          "did not render; check FreeSpinBasicReelSet's build guard.",
      );
      return;
    }

    // NEVER on a guess. rgsPlay checks flow.available_actions and refuses
    // with the API's own 202 rather than sending — see the header.
    spinningRef.current = true;
    manualStopRef.current = false;
    stoppedReelsRef.current.clear();
    dispatch(setFreeSpinsSpinning(true));
    // Every spin starts un-stopped. The reel reads this the moment it is asked
    // to land, so a flag left up from the last spin would give an unpressed
    // spin the hurried landing.
    dispatch(setFreeSpinsManualStop(false));

    // ── Lower it here, because nothing else will ──
    //
    // Nothing else lowers it. rgsPlay.pending clears the BASE spinComplete for
    // a base spin, and deliberately touches nothing of ours — the two rounds
    // are separate features and must never move each other's flags.
    //
    // But this flag is what the win controller gates on, and what the loop
    // above waits for. Left high it never edges false→true, so the win
    // controller's main effect is never re-entered and the loop believes the
    // previous spin is still the current one.
    //
    // The last reel to stop raises it again. Between the two calls sits
    // exactly one free spin, start to fully presented.
    dispatch(setFreeSpinsSpinComplete(false));

    // ── Lower the counter latch too ──
    //
    // ReelControlContext.handleSpin does exactly this before every base spin.
    // It is what makes the loop's "wait until finished" gate safe: the flag is
    // false for the whole spin and only the win controller raises it, at the end
    // of the presentation. Without the reset the flag is still true from the
    // PREVIOUS round when this spin lands, and the loop reads the last round's
    // answer for this one.
    dispatch(setCounterAnimationStarted(false));
    dispatch(setCounterAnimationFinished(false));

    // startReels();
    // ── This spin's speed, and turbo's start (2026-10-23) ── the base game's
    // handleSpin: turbo turns the reels on the press (SPIN_AFTER_WAIT_MS 0);
    // super turbo stays idle for its window — on a healthy connection the
    // reply beats it and the response effect DROPS the reels into place, so
    // nothing spins at all. Past the window they start turning, so a slow
    // server shows a working game.
    const speed = store.getState().settings?.autoPlaySpeed || "auto";
    spinSpeedRef.current = speed;
    turboSpinningRef.current = false;
    turboSpinTimerRef.current?.clear?.();
    turboSpinTimerRef.current = null;
    if (!isTurboLike(speed)) {
      startReels();
    } else {
      const waitMs = speedConfigFor(speed).SPIN_AFTER_WAIT_MS ?? 300;
      if (waitMs <= 0) {
        turboSpinningRef.current = true;
        startReels();
      } else {
        const timer = new TickerTimeout(() => {
          turboSpinTimerRef.current = null;
          // Already dropped, or a press asked to land: the stop path owns it.
          if (turboSpinningRef.current || !spinningRef.current || manualStopRef.current) return;
          turboSpinningRef.current = true;
          startReels();
        }, waitMs);
        turboSpinTimerRef.current = timer;
        track(timer);
      }
    }

    // ── The play button turns, on every free spin ──
    //
    // ReelControlContext.handleSpin does this immediately after its own
    // dispatch(rgsPlay(...)), and Pot Collection's loop does it before each
    // handleSpin. It was missing here entirely, which is why this button sat
    // still while the base game's turned. Secondary theme only, matching the
    // base game's check.
    if (playButtonRef.current && Settings.APP.UI_THEME === "secondary") {
      createPlayButtonRotationAnimation(playButtonRef.current);
    }

    // No .catch here on purpose. A refusal is not handled by this call site:
    // rgsPlay.rejected arms spinSlice's block so nothing further can be sent,
    // the response effect freezes the reels, and the recovery flow resyncs.
    // The console.log that used to hang off .then/.catch reported none of that
    // and shipped.
    dispatch(rgsPlay({ freeSpin: true }));
  }, [dispatch, startReels, freeSpinBasicReelRefs, playButtonRef, track]);

  /**
   * The player asked for the reels to land now.
   *
   * The ONLY interaction this round has. It does not skip the spin or the
   * round — it swaps the stop cascade for the tighter one. If the response
   * has not arrived yet the request stands; the response effect will stop the
   * reels the moment it does.
   */
  const handleManualStop = useCallback(() => {
    if (!spinningRef.current) {
      // Nothing is turning. The base game starts a spin here; this room's loop
      // owns that, so there is simply nothing to do.
      return;
    }

    // ── A press during the anticipation skips ONE reel ──
    //
    // The base game's rule (ReelControlContext.handleManualStop): while a
    // chain reel is on its turn, a press cuts that reel only and the chain
    // hands on to the next. Checked BEFORE the one-stop latch below, so every
    // press during the chain lands one reel instead of the first spending the
    // spin's only stop.
    if (makeAnticipationRunner().skipActiveReel()) {
      return;
    }

    // ── ONE MANUAL STOP PER SPIN ──
    //
    // The base game's first line in its own manual stop: ReelControlContext
    // opens with `if (stopTriggeredRef.current) return;` and never revisits the
    // decision for that spin. This is that latch, and it must stay ABOVE
    // stopReels.
    //
    // Without it, rapid presses hung the last reel. stopReels CANCELS every
    // per-reel stop still in the air and re-schedules the lot at
    // REEL_STOP_DELAYS_MANUAL — 0/50/100/150/200. Reel 5 is the 200ms entry, so
    // any press arriving sooner than that destroyed its timer and started the
    // clock again. It landed only once the player stopped pressing. Spacebar
    // made it certain: key auto-repeat fires every ~30ms.
    //
    // The old code KNEW the press was a repeat — it read the flag into
    // `alreadyManual` — and used it only to skip a dispatch, then re-scheduled
    // anyway. Re-scheduling to the same DELAY is not the same TIME.
    if (manualStopRef.current) {
      return;
    }
    manualStopRef.current = true;
    // What makes the reels SOUND stopped rather than merely arrive — see the
    // reducer. The base game gets this from spin.manualStop, which stays false
    // for the whole free-spin round.
    dispatch(setFreeSpinsManualStop(true));

    // ── LAND THEM NOW, wherever the spin had got to ──
    //
    // Three states this press can arrive in, and all three answer it:
    //
    //   reply not in yet   nothing to land. manualStopRef is armed, and the
    //                      response effect reads it the moment the reply
    //                      arrives and lands them on the manual cascade — see
    //                      the `wait` line, which is why refusing later presses
    //                      cannot strand this case.
    //   reply in, waiting  a stop was scheduled for the end of the minimum
    //                      spin time. stopReels cancels that timer.
    //   reply in, stopping the AUTO cascade is already running — 0/200/400/
    //                      600/800ms. stopReels cancels every per-reel stop
    //                      still in the air and re-schedules them at 0/50/100/
    //                      150/200. This is the case that used to do NOTHING,
    //                      and it is most of the spin.
    const round = store.getState().freeSpins?.currentRound;
    const replyIsIn = !!round?.reels && round !== roundAtStartRef.current;
    if (replyIsIn) stopReels(true);
  }, [dispatch, stopReels, makeAnticipationRunner]);

  // ── The response ─────────────────────────────────────────────────────────
  //
  // Subscribed directly to the store rather than through a selector, so the
  // stop fires on the dispatch itself instead of a render later. Same reason
  // ReelControlContext subscribes for its own response detection.
  useEffect(() => {
    if (!spinning) return;

    const startedAt = Date.now();
    const minSpinMs = FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO.SPIN_DURATION;
    let stopped = false;
    // The board on screen as this spin leaves. Anything else means the
    // response for THIS spin has landed — see tryStop below.
    const roundAtStart = store.getState().freeSpins?.currentRound;
    // Shared with handleManualStop, which needs the same test to know whether
    // there is anything to land yet.
    roundAtStartRef.current = roundAtStart;
    // A handle from the PREVIOUS spin must never be reachable from this one.
    pendingStopTimerRef.current = null;

    // ── Slow network safety ──
    //
    // PotCollectionSpinEngine's _connectionTimeout, ported. A TickerTimeout so
    // a hidden tab freezes it with the rest of the pipeline. Before this, a
    // request that never settled left the reels turning for ever and the only
    // trace was a console.log.
    let connectionTimeout = new TickerTimeout(() => {
      connectionTimeout = null;
      if (!store.getState().userInterface?.connectionPopupActive) {
        dispatch(openConnectionPopup());
      }
    }, minSpinMs + CONNECTION_TIMEOUT_BUFFER);

    const clearConnectionTimeout = () => {
      connectionTimeout?.clear?.();
      connectionTimeout = null;
      // ── ...and take the popup down with it ──
      //
      // Opening without closing is half a feature: PotCollectionSpinEngine
      // closes it the moment the response lands, and so does the base game.
      // Left open it survives into the next spin and into the base game, where
      // selectAnyPopupActive keeps the play button dead.
      if (store.getState().userInterface?.connectionPopupActive) {
        dispatch(closeConnectionPopup());
      }
    };

    const tryStop = () => {
      if (stopped) return;
      const state = store.getState();

      // ── A refused free spin freezes the board ──
      //
      // The base game's answer to an error, and Pot Collection's: freeze where
      // they are, no landing, no sound, and raise reelsFrozen so the recovery
      // flow (warning popup -> ErrorRecoveryOverlay -> refreshGameData) has
      // something to key on. That resync is also the ONLY thing that clears
      // spinSlice's playBlockedUntilResync, so this is what lets the round
      // resume after a refusal instead of retrying into a ban.
      //
      // spinning is lowered so this subscription and the landing poll end;
      // spinComplete is deliberately LEFT false, so the loop cannot schedule
      // another spin on the way out.
      if (state.spin.featureSpinStatus === "failed") {
        stopped = true;
        unsubscribe?.();
        clearConnectionTimeout();
        clearTimers();
        const reels = freeSpinBasicReelRefs.current || [];
        for (let i = 0; i < reels.length; i++) reels[i]?.freezeSpin?.();
        soundManager.stop(SOUND_NAME.reel_spin);
        spinningRef.current = false;
        dispatch(setReelsFrozen(true));
        dispatch(setFreeSpinsSpinning(false));
        return;
      }
      // ── THIS SPIN's board, not merely SOME board ──
      //
      // The base game tests `spin.status === "succeeded"`, which its own
      // rgsPlay.pending resets to "pending" first — so the test can only pass
      // on the response to the spin just sent. Free spins has no such per-spin
      // status it can trust (`featureSpinStatus` is forced to "succeeded" by
      // setFeatureSpinComplete from a dozen unrelated call sites — see the note
      // on rgsPlay's condition), and `freeSpins.currentRound` is REPLACED on
      // each response but never cleared at spin start.
      //
      // So the test is the object itself. rgsPlay.fulfilled assigns a new round
      // object every time, and identity is exact: while this is still the round
      // we started the spin on, the response has not landed. Reading only
      // `?.reels` passed instantly from the second free spin onward, which is
      // what let a stop land the reels on the PREVIOUS board.
      const round = state.freeSpins?.currentRound;
      if (!round?.reels || round === roundAtStart) return;

      stopped = true;
      unsubscribe?.();
      clearConnectionTimeout();

      // ── Turbo, the reply inside the window: the DROP (2026-10-23) ── the
      // reels never started, so start and stop them in the same breath — they
      // travel only the drop distance and fall into place (the base game's
      // turbo drop).
      if (isTurboLike(spinSpeedRef.current) && !turboSpinningRef.current) {
        turboSpinTimerRef.current?.clear?.();
        turboSpinTimerRef.current = null;
        turboSpinningRef.current = true;
        pendingStopTimerRef.current = null;
        startReels();
        stopReels(manualStopRef.current);
        return;
      }

      // The response and the minimum spin time race, and the LATER one wins —
      // a fast reply still gets a full spin, a slow one is never cut short.
      // Identical to the reveal's own race in GambleDialogController.
      const elapsed = Date.now() - startedAt;
      // const wait = manualStopRef.current ? 0 : Math.max(0, minSpinMs - elapsed);
      // Turbo keeps its OWN minimum (5 ms — landed the moment the reply is
      // in), never AUTO's: the base game's rule (2026-10-23).
      const stopMinMs = isTurboLike(spinSpeedRef.current)
        ? speedConfigFor(spinSpeedRef.current).MIN_SPIN_DURATION ?? 0
        : minSpinMs;
      const wait = manualStopRef.current ? 0 : Math.max(0, stopMinMs - elapsed);

      if (wait === 0) {
        pendingStopTimerRef.current = null;
        stopReels(manualStopRef.current);
      } else {
        // Held as well as tracked: the response is IN, and a manual press from
        // here on must be able to take this over rather than wait it out.
        const timer = new TickerTimeout(() => {
          pendingStopTimerRef.current = null;
          stopReels(false);
        }, wait);
        pendingStopTimerRef.current = timer;
        track(timer);
      }
    };

    const unsubscribe = store.subscribe(tryStop);
    tryStop();

    return () => {
      unsubscribe?.();
      clearConnectionTimeout();
      pendingStopTimerRef.current = null;
    };
  }, [
    spinning,
    stopReels,
    startReels,
    track,
    dispatch,
    clearTimers,
    freeSpinBasicReelRefs,
  ]);

  // ── Landing ──────────────────────────────────────────────────────────────
  //
  // Polled on the ticker rather than driven by a per-reel callback, because
  // `setOnReelLanded` is already claimed by the landing-animation hook and
  // two owners on one callback is how the base game's reels lost animations
  // in the past.
  useEffect(() => {
    if (!spinning) return;

    const poll = new TickerTimeout(function check() {
      if (!spinningRef.current) return;
      // ── A turbo spin whose reels have not started is NOT landed (2026-10-23
      // fix) ── super turbo keeps them still until the reply (or its window),
      // and still-standing reels report "complete" from the LAST spin — read
      // as landed, this ended the spin before the reply and the round froze.
      if (isTurboLike(spinSpeedRef.current) && !turboSpinningRef.current) {
        track(new TickerTimeout(check, 50));
        return;
      }
      if (allReelsLanded()) {
        spinningRef.current = false;
        dispatch(setFreeSpinsSpinning(false));
        // ── The round does NOT count its own spins ──
        //
        // `played` is derived from the server on every response:
        //   state.played = awarded - features.freespins_left
        // and the reducer's own comment says counters come from the server and
        // must never be counted locally — `freespins_issued` GROWS mid-round
        // when a level is reached, so a local tally cannot stay right.
        //
        // Incrementing here as well double-counted: the response set played=N,
        // then this added one more, so `remaining` (awarded - played) hit zero a
        // spin early. The round showed its total win and wiped back to base
        // while the server still held freespins_left = 1 — leaving flow.state on
        // "freespins", so the next base spin was not in available_actions and
        // got refused. Two refusals is the permanent session lock.
        return;
      }
      track(new TickerTimeout(check, 50));
    }, 50);
    track(poll);

    return () => poll.clear?.();
  }, [spinning, allReelsLanded, dispatch, track]);

  // ── The loop ─────────────────────────────────────────────────────────────
  //
  // One effect drives the whole round: whenever the room is up and nothing is
  // spinning, either the next spin is scheduled or the round is over. The
  // first spin is the same path as every other, just with a longer delay so
  // the player sees the room they have arrived in.
  useEffect(() => {
    if (!active) return;

    // ── The gate is spinComplete, exactly as it is for base autoplay ──
    //
    // NOT `!spinning`. `spinning` clears when the reels mechanically land,
    // which is the moment BEFORE the win controller has even looked at the
    // round. Scheduling from there is what let a spin fire over the top of a
    // celebration that had not started yet.
    //
    // spinComplete is raised by the last reel's stop and lowered by
    // handleFreeSpin, so it brackets the whole presentation — the same
    // contract ReelControlContext and useWinController hold each other to.
    if (
      fsBlockedBy("loop", [
        ["spinning", spinning],
        ["!spinComplete", !spinComplete],
      ])
    ) {
      return;
    }

    // TEMPORARY, dev only — see FS_HOLD_ON_FIRST_ROUND. Placed high so it
    // reads as what it is: nothing below this runs, and the round simply sits
    // on the board it arrived with.
    if (
      FEATURES_CONFIG.DEV_TOOLS.FS_HOLD_ON_FIRST_ROUND &&
      fsBlockedBy("loop", [["FS_HOLD_ON_FIRST_ROUND", true]])
    ) {
      return;
    }

    // Nothing starts while a wipe is on screen — not the first spin, and not
    // the outro. The effect re-runs the moment the flag clears, so the delay
    // below is measured from the cover lifting rather than from the midpoint.
    if (fsBlockedBy("loop", [["transitionActive", transitionActive]])) return;

    // Hard block: while an error popup or the recovery overlay is up, NO spin
    // triggers whatsoever. First test in the chain, exactly as
    // PotCollectionControlContext orders it.
    if (
      fsBlockedBy("loop", [
        ["introPopupActive", introPopupActive],
        ["additionalFreeSpinsActive", additionalFreeSpinsActive],
        // A Hold & Win from a free spin owns the round (holdAndWinOwnsRound).
        ["holdAndWin", holdAndWinOwnsRound],
        // Cleopatra's Fire retrigger: spins the popup has not announced yet —
        // the next spin waits for the popup (useFreeSpinBasicWinController's
        // retrigger branch), which releases them on its idle beat.
        [
          "unannouncedSpins",
          store.getState().freeSpins.unannouncedSpins > 0,
        ],
        // Hidden tab. The base game's autoplay does exactly this — see the
        // isActive test in ReelControlContext's autoplay effect. Returning
        // WITHOUT scheduling matters: a timer armed now would be frozen with
        // the ticker and fire late on return, stacking a spin behind whatever
        // resumed first.
        ["appInactive", !isActive],
        ["errorPopupActive", errorPopupActive],
        ["errorRecoveryTransitionActive", errorRecoveryTransitionActive],
        ["spinHeldOff", spinHeldOffNow],
        // ── A refused free spin stops the loop dead ──
        //
        // THE BAN GUARD, and the reason this line exists: after a refusal our
        // state provably disagrees with the server, so the next command built
        // from it may be illegal too. The server answers a second illegal
        // action with `104 locked` — session dead, `init` included. Five
        // players were banned this way on 2026-08-14.
        //
        // rgsPlay's `condition` would refuse the request anyway; this is the
        // same belt Pot Collection wears (commit e7230589), and it also stops
        // the loop pretending to spin and desyncing the visuals.
        //
        // Read from the store, not a selector: this effect must see the value
        // as it is NOW, not as it was when React last rendered.
        [
          "playBlockedUntilResync",
          store.getState().spin.playBlockedUntilResync,
        ],
      ])
    ) {
      return;
    }

    // Something owns the screen. Return WITHOUT scheduling, and without
    // holding a timer: the effect re-runs when this clears and decides again.
    if (fsBlockedBy("loop", [["criticalAnimationPlaying", criticalAnimationPlaying]]))
      return;

    // The entry flourish owns the screen until its last box has converted.
    // Returns WITHOUT scheduling, so the effect re-decides the moment it clears
    // rather than racing a timer against it.
    // Mad Labs only — no level boxes to convert in Cleopatra's Fire.
    // if (fsBlockedBy("loop", [["entrySequencePending", entrySequencePending]]))
    //   return;

    // ── A reloaded round resumes on a PRESS, never on its own ──
    //
    // True from the hydrate, so it is already set before the scene mounts —
    // otherwise the loop fired a spin in the background while the reload popup
    // was still on screen. Pot Collection has the same contract: its loop never
    // self-starts, and the first button press begins it.
    if (fsBlockedBy("loop", [["awaitingResume", awaitingResume]])) return;

    // ── One round at a time ──
    //
    // The old loop set a TickerTimeout and then returned early on every
    // re-render because the ref was non-null. Nothing could cancel it, so a
    // win that began during those 400ms was simply spun over. This ref only
    // stops a duplicate being made in the same state; the cleanup below is
    // what actually cancels, and it runs on every dependency change.
    if (fsBlockedBy("loop", [["processingRef", processingRef.current]])) return;

    const roundWin = store.getState().freeSpins.currentRound?.win || 0;

    // ── Winning round: the presentation sets the pace ──
    //
    // Base autoplay's rule, and it splits on whether the round PAID:
    //
    //   no win   no presentation to wait for — schedule on the delay below.
    //   a win    wait for `counterAnimationFinished && !winAnimationsPlaying`,
    //            then spin with no extra delay. The presentation IS the pause.
    //
    // Requiring finished === TRUE is the part that matters, and the earlier
    // `counterStarted && !counterFinished` was wrong for a reason that only
    // shows up as a race. freeSpins.spinComplete is raised by the last reel to
    // stop; the win controller's main effect then starts its presentation
    // ASYNCHRONOUSLY. In the frames between those two, nothing has dispatched
    // counterAnimationStarted yet — so "started but not finished" was false,
    // this gate passed, and with the zero delay a paying round takes below the
    // next spin was already away before the show-all had drawn a single frame.
    // That is the missing pause and the win text that never appeared.
    //
    // Safe against stalling only because handleFreeSpin now lowers the flag at
    // spin start and the win controller raises it on EVERY path, no-win rounds
    // included — the same contract the base game runs on.
    if (
      fsBlockedBy("loop", [
        ["winAnimationsPlaying", winAnimationsPlaying],
        ["win pending counter", roundWin > 0 && !counterFinished],
      ])
    ) {
      return;
    }

    fsDiag("loop -> scheduling next spin", {
      remaining,
      roundWin,
      played: store.getState().freeSpins.played,
      awarded: store.getState().freeSpins.awarded,
    });

    const schedule = (delay, run) => {
      // ── Raised HERE, and nowhere else ──
      //
      // So it can only ever be true when a cleanup exists to lower it again.
      //
      // It used to be raised before the branching below, which had one path
      // that returns WITHOUT a cleanup: the completion-reload branch, where the
      // scene owns the total-win dialog. React only runs a cleanup the effect
      // actually returned, so on that path the flag stayed raised for good —
      // and the NEXT free-spins round was blocked by processingRef on its very
      // first pass and never spun. One round per session, silently.
      processingRef.current = true;
      const timeout = new TickerTimeout(() => {
        nextSpinTimeoutRef.current = null;
        // ── LET THE BOARD FINISH LANDING FIRST ──
        //
        // The base game's autoplay does exactly this, through the same helper.
        // The next free spin clears track 0 — the track the landing bounce is
        // playing on — so a bounce still in the air was cut off. Always the last
        // reel, because the reels stop 200ms apart and only the final one is
        // still moving when a fixed delay expires.
        //
        // Nothing to wait for costs nothing: a board with no landing clips
        // resolves on the next microtask, so the loop's cadence is unchanged
        // wherever nothing is animating.
        waitForBoardToSettle(
          freeSpinBasicOverlayReelRefs.current?.map((r) =>
            r?.getOverlayReelController?.(),
          ),
          {
            fallbackMs: delay,
          },
        ).then(() => {
          // Released on the far side, not before: processingRef is what stops
          // the loop scheduling a SECOND spin, and the wait is real time in
          // which the effect can re-run.
          processingRef.current = false;
          // The round can end, be interrupted, or have a spin already started
          // while the board settles. Same re-check the base game makes after
          // its own wait, and for the same reason: the decision to spin was
          // made before the wait, and the wait is real time.
          const st = store.getState();
          if (!st.freeSpins.active || spinningRef.current) {
            fsDiag("loop -> spin WITHHELD after settle", {
              active: st.freeSpins.active,
              spinning: spinningRef.current,
            });
            return;
          }
          run();
        });
      }, delay);
      nextSpinTimeoutRef.current = timeout;
      return () => {
        timeout.clear();
        if (nextSpinTimeoutRef.current === timeout) {
          nextSpinTimeoutRef.current = null;
        }
        processingRef.current = false;
      };
    };

    const capReached = isFreeSpinsCapReached(store.getState());

    if (!isFreeSpinsRoundFinished(remaining, capReached)) {
      // ── Re-checked at FIRE time, not only here ──
      //
      // The gates above ran when the spin was DECIDED. The delay below is 0 on
      // a winning round and INTER_SPIN_DELAY otherwise, and the mystery reveal
      // raises criticalAnimationPlaying from a different effect on this same
      // spinComplete tick — so a spin cleared here can still land inside a
      // reveal that had not started yet when it was cleared.
      //
      // Skipping is safe: criticalAnimationPlaying is in this effect's deps,
      // so clearing it re-runs the loop and the spin is scheduled again.
      // processingRef is already false by the time run() is called.
      const fireSpin = () => {
        if (store.getState().animations.criticalAnimationPlaying) {
          fsDiag("loop -> spin withheld, presentation still holding");
          return;
        }
        handleFreeSpin();
      };

      const isFirst = store.getState().freeSpins.played === 0;

      // ── The entry flourish IS the pause ──
      //
      // ENTRY_DELAY exists to stop the round starting while the wipe is still
      // lifting. When boxes were banked, the flourish covers that and more:
      // this effect is gated on entrySequencePending, so by the time it gets
      // here the pips have flown, the last box has converted and the clip has
      // ended on its own completion. Adding a second of nothing after that is
      // a second of nothing.
      //
      // Armed on exactly the test TransitionDialog uses to arm the flourish,
      // so the two cannot disagree about whether one ran.
      // Mad Labs only — Cleopatra's Fire has no flourish, the first spin
      // always takes ENTRY_DELAY.
      // const hadEntryFlourish =
      //   (store.getState().freeSpins.serverLevel ?? 1) > 1;

      // A won round has already paused on its counter, so it goes straight
      // into the next spin — again, base autoplay's own rule.
      // const delay = isFirst
      //   ? (hadEntryFlourish ? 0 : ENTRY_DELAY)
      //   : roundWin > 0
      //     ? 0
      //     : INTER_SPIN_DELAY;
      // const delay = isFirst
      //   ? ENTRY_DELAY
      //   : roundWin > 0
      //     ? 0
      //     : INTER_SPIN_DELAY;
      // Turbo's own gap between spins (SPIN_DELAY 150 / 75), as base turbo
      // autoplay (2026-10-23).
      const speedNow = store.getState().settings?.autoPlaySpeed;
      const gap = isTurboLike(speedNow)
        ? speedConfigFor(speedNow).SPIN_DELAY ?? INTER_SPIN_DELAY
        : INTER_SPIN_DELAY;
      const delay = isFirst
        ? ENTRY_DELAY
        : roundWin > 0
          ? 0
          : gap;
      return schedule(delay, fireSpin);
    }

    // ── Out of spins ──
    //
    // ONE owner. On a completion reload the SCENE opens this dialog, after the
    // intro popup has closed and the room has been seen — see the completion
    // branch in GameViewSimple. The loop opening it as well is what put it on
    // top of the intro: this effect runs long before the scene's boot gate
    // does, so the loop always won the race.
    if (reloadPhase === "completion") {
      fsDiag("loop -> out of spins, deferring to scene (completion reload)");
      return;
    }

    // ── ONE popup closes the round, and it is chosen in one place ──
    //
    // MAX WIN replaces TOTAL WIN, it does not precede it. Pot Collection makes
    // exactly this swap at the end of its own bonus — see the winLimitReached
    // branch in PotCollectionSpinEngine._startEndOfBonusSequence.
    //
    // ── Reached here with spins LEFT, when the cap did it ──
    //
    // This used to be the out-of-spins branch and nothing else, on Zeus's rule
    // that the cap never ends a round early. It does now: the branch above
    // refuses to schedule another spin the moment the cap is hit, and the round
    // falls through to here with whatever the counter still said. Everything
    // from this line on is the ordinary ending, unchanged — the popup, the
    // wipe, the teardown. See isFreeSpinsRoundFinished.
    //
    // BETTER LUCK NEXT TIME is the third outcome, on the same swap: a round
    // paying under 1x the stake shows it INSTEAD of the total. Same slot, same
    // spine, different skin.
    //
    // The test is the ROUND's total, not `roundWin` — that is only the last
    // spin, and a round can end on a blank spin after paying well.
    const outroPopup = selectFreeSpinOutroPopup(store.getState());
    const outroAction =
      outroPopup === FS_OUTRO_POPUP.MAX
        ? setShowFreeSpinMaxWin
        : outroPopup === FS_OUTRO_POPUP.ZERO
          ? setShowFreeSpinZeroWin
          : setShowFreeSpinTotalWin;

    // A live round: the loop owns it, and closing the dialog starts the wipe
    // back — the same handover the intro does on the way in.
    fsDiag(`loop -> out of spins, opening ${outroPopup} win`, {
      remaining,
      roundWin,
    });
    // ── A WON last spin has already had its beat ──
    //
    // The gates above do not let this run until the presentation is over:
    // winAnimationsPlaying is down and, on a paying round, the counter has
    // finished. So the player has JUST watched the global win hold for its two
    // loops — and waiting the same two loops again on top read as the round
    // forgetting to end. Two beats where the design has one.
    //
    // So the delay only applies where there was nothing to watch. Exactly the
    // rule the next-spin delay below already follows: "A won round has already
    // paused on its counter, so it goes straight into the next spin."
    //
    // An animated win, a mystery or the Golden Mystery are all covered by the
    // same gates — they hold criticalAnimationPlaying or the counter, so this
    // line is not reached until they are done.
    return schedule(roundWin > 0 ? 0 : outroDelayMs(), () =>
      dispatch(outroAction(true)),
    );
  }, [
    active,
    spinning,
    spinComplete,
    // Stable ref object from useReelRefs — listed for the linter, not because
    // it can change. Used by the settle wait inside schedule().
    freeSpinBasicOverlayReelRefs,
    remaining,
    winLimitReached,
    fsTotalWin,
    transitionActive,
    criticalAnimationPlaying,
    // entrySequencePending,
    awaitingResume,
    winAnimationsPlaying,
    counterFinished,
    introPopupActive,
    additionalFreeSpinsActive,
    isActive,
    reloadPhase,
    errorPopupActive,
    errorRecoveryTransitionActive,
    spinHeldOffNow,
    holdAndWinOwnsRound,
    handleFreeSpin,
    dispatch,
    playButtonRef,
  ]);

  // ── The waiting glow ──────────────────────────────────────────────────────
  //
  // A copy of PotCollectionControlContext's pulse, in the same place: the gate
  // that refuses to self-start. It runs while the round is parked and the reload
  // popup has been dismissed, which is exactly when the press will be accepted —
  // the glow is a promise that pressing does something, so it must not appear
  // while handleResume would still refuse.
  //
  // Brightness, not scale: 1.0 to 2.5 over 300ms, easeInOutSine, alternating.
  // PC's numbers, so both features pulse identically.
  //
  // The filter is re-attached on every tick because the button's hover handlers
  // assign sprite.filters outright — a pointer crossing the button mid-pulse
  // would otherwise kill the glow for good. That guard is PC's, and it is the
  // part that is not obvious.
  useEffect(() => {
    if (!active) return;
    if (!awaitingResume || reloadPopupPending) return;

    const sprite = playButtonRef.current;
    if (!sprite) return;

    const pulseFilter = new PIXI.ColorMatrixFilter();
    pulseFilter.resolution = window.devicePixelRatio || 1;
    pulseFilter.padding = 8;
    sprite.filters = [pulseFilter];

    const proxy = { brightness: 1.0 };
    const pulse = anime({
      targets: proxy,
      brightness: 2.5,
      duration: 300,
      easing: "easeInOutSine",
      direction: "alternate",
      loop: true,
      update: () => {
        if (!sprite || sprite.destroyed) {
          pulse.pause();
          return;
        }
        if (!sprite.filters || !sprite.filters.includes(pulseFilter)) {
          sprite.filters = [pulseFilter];
        }
        pulseFilter.brightness(proxy.brightness, false);
      },
    });
    pulseRef.current = pulse;

    return () => {
      pulse.pause();
      anime.remove(proxy);
      pulseRef.current = null;
      // Hand the sprite back clean — hover owns filters from here.
      if (sprite && !sprite.destroyed) sprite.filters = [];
    };
  }, [active, awaitingResume, reloadPopupPending, playButtonRef]);

  /**
   * Resume a reloaded round — the free-spin twin of Pot Collection's
   * startAutoplay, and copied from it including the part that matters.
   *
   * Called by the play button's press and by spacebar below. Both inputs, one
   * function, so the two can never drift.
   *
   * ── IT FIRES THE FIRST SPIN ITSELF ──
   *
   * Clearing the gate is not enough, and this is the bug that made a glowing
   * button do nothing when pressed. startAutoplay says it outright:
   *
   *   // Fire first spin directly — can't rely on the React effect
   *   // re-triggering because refs don't cause re-renders.
   *
   * Ours does cause a re-render, so the loop effect DOES re-run — but re-running
   * is not the same as scheduling. The effect is a chain of early returns, and
   * on that pass one of them still holds, so it returns having done nothing and
   * then has no reason to run again: nothing else in the round is going to
   * change state while it sits parked. The press is the only event there is, so
   * the press has to do the work.
   *
   * The delay matches the loop's own between-spins pacing, so resuming looks
   * like the round simply carrying on.
   */
  const handleResume = useCallback(() => {
    const state = store.getState();

    // [FSResume] TEMP DIAGNOSTIC — remove once the press is proven.

    // The same three refusals PC's startAutoplay opens with: the popup still
    // owns the screen, the round is over, or the press already landed.
    if (state.freeSpins.reloadPopupPending) {
      return;
    }
    if (state.animations.criticalAnimationPlaying) {
      return;
    }
    if (!state.freeSpins.awaitingResume) {
      return;
    }
    if (state.freeSpins.awarded - state.freeSpins.played <= 0) {
      return;
    }

    // Lowers the gate for every LATER spin, and stops the glow: the button
    // derives its pulse from this flag, so clearing it retires the pulse and
    // restores the sprite's filters through the effect's own cleanup — the
    // declarative equivalent of PC pausing pulseIntervalRef by hand.
    dispatch(setFreeSpinsAwaitingResume(false));

    // Stop the pulse now rather than waiting for the effect cleanup — the same
    // two lines startAutoplay ends its glow with, so the button stops glowing on
    // the press itself and never for a frame longer.
    if (pulseRef.current) {
      pulseRef.current.pause();
      pulseRef.current = null;
    }
    if (playButtonRef.current) {
      playButtonRef.current.filters = [];
    }

    // Claimed BEFORE the dispatch settles, so the loop effect — which re-runs on
    // that state change and checks this same ref first — cannot also schedule a
    // spin. One press, one spin.
    if (processingRef.current) {
      return;
    }
    processingRef.current = true;

    // ── Straight into the spin, no timer ──
    //
    // Base ButtonPlay's press does exactly this: rotate, then handleSpin, same
    // tick. Every millisecond here is felt as lag because the player is already
    // waiting on their own press.
    //
    // Safe to call synchronously: handleFreeSpin sets spinningRef and dispatches
    // freeSpins.spinning itself, both before this returns, so the loop effect —
    // which re-runs on the dispatch above — sees a spin in flight and returns at
    // its own gate. A second press cannot double-spin for the same reason.
    processingRef.current = false;
    handleFreeSpin();
  }, [dispatch, handleFreeSpin, playButtonRef]);

  /**
   * One press, resolved the way the base game resolves it.
   *
   * ButtonPlay does NOT simply stop reels — `resolveButtonAction` ranks
   * SKIP_ANIMATIONS *above* STOP_SPIN, so a press during a celebration skips
   * the presentation instead of being thrown away. This board had no skip path
   * at all, which is why the press worked exactly once per spin: the first one
   * caught a spinning reel, and every later one arrived while a reveal or a win
   * owned the screen and was refused by handleManualStop's own !spinningRef
   * gate with nothing to stop.
   *
   * Same order as ButtonPlay, minus the two priorities that cannot occur here
   * (starting a spin, and the autoplay-turbo no-op — this round has no button
   * that starts anything and one speed).
   *
   *   1  a reloaded round is parked      → resume it
   *   2  a celebration is on screen      → skip it
   *   3  the reels are turning           → land them sooner
   */
  const handlePress = useCallback(() => {
    const state = store.getState();

    // Priority 1: the parked round. handleResume refuses on its own if the
    // reload popup is still up.
    if (state.freeSpins.awaitingResume) {
      soundManager.play(SOUND_NAME.button_click);
      handleResume();
      return;
    }

    // Priority 2: skip the celebration. ButtonPlay's executeSkipAnimations,
    // verbatim — the two flags are not interchangeable: one dismisses a screen
    // that is WAITING on the player, the other fast-forwards one that is not.
    if (state.animations.winAnimationsPlaying) {
      soundManager.play(SOUND_NAME.button_click);
      if (state.animations.isWaitingForUserConfirmation) {
        dispatch(setUserWantsToDismissWinAnimation(true));
      } else {
        dispatch(setSkipWinAnimation(true));
      }
      return;
    }

    // ── Priority 2b: THE REELS WIN THIS ARGUMENT ──
    //
    // Ranked ABOVE the critical-animation gate, which is the base game's own
    // order: ButtonPlay resolves STOP_SPIN off the spinning state and only
    // falls through to a refusal when there is no spin to stop.
    //
    // The gate used to sit above this, so a reveal still on screen from the
    // LAST spin swallowed a press meant for THIS one — silently, with no click,
    // which is exactly what "manual stop sometimes fails" was.
    if (spinningRef.current) {
      soundManager.play(SOUND_NAME.button_click);
      handleManualStop();
      return;
    }

    // Nothing is turning and a reveal owns the screen — the base game's own
    // refusal, ButtonPlay returns on this too. NOT remembered for later: a
    // press belongs to the spin it was made during, and carrying it forward
    // auto-stopped the NEXT spin, which is its own kind of unresponsive.
    if (state.animations.criticalAnimationPlaying) {
      return;
    }

    // Priority 3: land the reels. The click is played here rather than inside
    // handleManualStop so the press is always audible, which is what ButtonPlay
    // does in executeStopSpin.
    soundManager.play(SOUND_NAME.button_click);
    handleManualStop();
  }, [dispatch, handleResume, handleManualStop]);

  // ── Keyboard input ───────────────────────────────────────────────────────
  //
  // Spacebar, for the same two actions the button has, in the same priority
  // order Pot Collection uses: resume a parked round first, land the reels
  // second. The button routes here too, through the context value. Only while
  // the round owns the screen, so the base game's own handler is never
  // shadowed.
  useEffect(() => {
    // if (!active) return;
    // Not under a Hold & Win won here: its room has its own spacebar.
    // if (!active || holdAndWinOwnsRound) return;
    // ── ...but the TRIGGER spin is still ours ── holdAndWin.triggered goes
    // up on the response, while this spin's reels (and its coin anticipation
    // chain) still turn — the listener came off right then, so the spacebar
    // could not skip the anticipation (Vladimir, 2026-10-08). It now stays on
    // while the round is active and steps aside only once nothing of ours
    // is spinning.
    if (!active) return;

    const onKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      const hw = store.getState().holdAndWin;
      if ((hw.triggered || hw.active) && !spinningRef.current) return;
      event.preventDefault();

      // ── A HELD key is not a second press ──
      //
      // The base game filters this in both of its keyboard paths — ButtonPlay
      // opens with `if (spacePressed && event.repeat) return;` and says why:
      // "prevents duplicate stops from key repeat". Holding space delivers a
      // keydown every ~30ms, so without this one held key reads as thirty
      // presses a second. The same filter is on the reveal skip hook.
      if (event.repeat) return;

      handlePress();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  // }, [active, handlePress]);
  // }, [active, holdAndWinOwnsRound, handlePress]);
  }, [active, handlePress]);

  // ── Leaving the round ────────────────────────────────────────────────────
  useEffect(() => {
    if (active) return;
    clearTimers();
    spinningRef.current = false;
    manualStopRef.current = false;
    // Belt and braces for the same leak schedule() now prevents: a round must
    // never hand the next one a raised gate.
    processingRef.current = false;
    stoppedReelsRef.current.clear();

    // Kill every tween still pointed at the SHARED play-button sprite before
    // ToolbarBottom swaps this button back for the base one. PC's autoplay
    // cleanup ends the same way, and for the reason its comment gives: an
    // in-flight rotation that keeps ticking after the sprite is destroyed throws
    // on the next frame. The sprite outlives us — the base game owns it next.
    if (pulseRef.current) {
      pulseRef.current.pause();
      pulseRef.current = null;
    }
    if (playButtonRef.current) {
      anime.remove(playButtonRef.current);
      anime.remove(playButtonRef.current.scale);
      playButtonRef.current.rotation = 0;
      playButtonRef.current.filters = [];
    }
  }, [active, clearTimers, playButtonRef]);

  useEffect(() => clearTimers, [clearTimers]);

  /**
   * App activity — pause on hide, and a safety net on return.
   *
   * Freezing is AUTOMATIC and needs nothing here: AppActivityContext sets
   * ticker.speed = 0, so the reel's deltaMS accumulation, every TickerTimeout
   * in this file and all spine updates stop together and resume with no
   * wall-clock drift.
   *
   * What is NOT automatic is the case that stranded the round. If the spin was
   * mid-flight when the tab went away — or its stop request was dropped while
   * frozen — nothing on resume goes looking for it and the reels spin for
   * ever. The base game has carried this net for a long time; this board never
   * got it.
   *
   * The timeout is a wall-clock setTimeout ON PURPOSE. A TickerTimeout would
   * be frozen by the very condition it exists to rescue.
   */
  useEffect(() => {
    if (!isActive) {
      pausedByInactivityRef.current = true;
      return undefined;
    }

    if (!pausedByInactivityRef.current) return undefined;

    // visibilitychange can fire in bursts; one rescue per return is plenty.
    const now = Date.now();
    if (
      lastTabReturnTimeRef.current &&
      now - lastTabReturnTimeRef.current < TAB_RETURN_DEBOUNCE_MS
    ) {
      return undefined;
    }
    lastTabReturnTimeRef.current = now;
    pausedByInactivityRef.current = false;

    if (!spinningRef.current) return undefined;

    fsDiag("activity: resumed mid-spin — arming emergency stop");
    emergencyStopRef.current = setTimeout(() => {
      emergencyStopRef.current = null;
      if (!spinningRef.current) return;
      fsDiag("activity: EMERGENCY STOP — still spinning after resume");
      // Plain, exactly as the base game's own tab-return rescue calls it
      // (ReelControlContext, the visibility effect). It is subject to the
      // one-press latch above, the same way that one is subject to
      // stopTriggeredRef. Do not add a bypass here without changing both.
      handleManualStop();
    }, EMERGENCY_RESUME_STOP_MS);

    return () => {
      if (emergencyStopRef.current) {
        clearTimeout(emergencyStopRef.current);
        emergencyStopRef.current = null;
      }
    };
  }, [isActive, handleManualStop]);

  const value = {
    handleFreeSpin,
    handleManualStop,
    handlePress,
    handleResume,
    awaitingResume,
    isSpinning: spinning,
    remaining,
  };

  return (
    <FreeSpinBasicControlContext.Provider value={value}>
      {children}
    </FreeSpinBasicControlContext.Provider>
  );
};

export const useFreeSpinBasicControl = () => {
  const ctx = useContext(FreeSpinBasicControlContext);
  // Null rather than throwing: the provider is only mounted when the feature
  // is on, and the play button is rendered either way.
  return ctx;
};

export default FreeSpinBasicControlContext;
